import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { MongoCustomerId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { CustomerBasicInfo } from "@redotech/effect-api-shared/models/customer";
import type { CustomerSearchItem } from "@redotech/merchant-sdk/rpc/schema/global-search/global-search-schema";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { useQuery } from "@tanstack/react-query";
import { ObjectId } from "bson";
import { useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useChangeCustomerConversationAction } from "../actions/use-conversation-actions";

export interface UseCustomerSelectorResult {
  customerListItems: RedoListItem<CustomerSearchItem | null>[];
  isLoading: boolean;
  dropdownOpen: boolean;
  badgeRef: HTMLElement | null;
  setBadgeRef: (ref: HTMLElement | null) => void;
  handleBadgeClick: () => void;
  handleDropdownOpenChange: (open: boolean) => void;
  handleItemSelected: (customer: CustomerSearchItem | null) => void;
  searchText: string;
  setSearchText: (text: string) => void;
}

function customerSearchItemToMongoCustomerId(
  customer: CustomerSearchItem,
): MongoCustomerId {
  return MongoCustomerId.fromObjectId(new ObjectId(customer.id));
}

export function useCustomerSelector({
  conversationId,
  currentCustomer,
}: {
  conversationId: ConversationId;
  currentCustomer: CustomerBasicInfo | undefined;
}): UseCustomerSelectorResult {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const changeCustomerAction = useChangeCustomerConversationAction();

  const [searchText, setSearchText] = useState("");
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const debouncedSearch = useDebounce(searchText, 300);

  const searchQuery = useQuery({
    queryKey: ["customerSearch", debouncedSearch],
    queryFn: async ({ signal }) => {
      if (!debouncedSearch.trim()) {
        return { results: [] as CustomerSearchItem[] };
      }
      const response = await rpcClient.getGlobalSearchResults(
        { searchText: debouncedSearch, category: "customers" },
        { signal },
      );
      return {
        results: response.results.filter(
          (item): item is CustomerSearchItem => item.type === "customer",
        ),
      };
    },
    enabled: dropdownOpen && debouncedSearch.trim().length > 0,
    staleTime: 30_000,
  });

  const customerListItems: RedoListItem<CustomerSearchItem | null>[] =
    useMemo(() => {
      const noCustomerItem: RedoListItem<null> = {
        type: "text",
        id: "no-customer",
        text: "No customer",
        value: null,
      };

      const customerItems: RedoListItem<CustomerSearchItem>[] = (
        searchQuery.data?.results ?? []
      ).map((customer) => ({
        type: "text",
        id: customer.id,
        text: customer.customerName,
        supportingText: customer.email ?? undefined,
        value: customer,
      }));

      return [noCustomerItem, ...customerItems];
    }, [searchQuery.data]);

  const handleBadgeClick = useHandler(() => {
    setDropdownOpen((open) => !open);
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchText("");
    }
  });

  const handleItemSelected = useHandler(
    (customer: CustomerSearchItem | null) => {
      const mongoCustomerId = customer
        ? customerSearchItemToMongoCustomerId(customer)
        : null;
      void changeCustomerAction.execute(conversationId, { mongoCustomerId });
      setDropdownOpen(false);
      setSearchText("");
    },
  );

  return {
    customerListItems,
    isLoading: searchQuery.isLoading,
    dropdownOpen,
    badgeRef,
    setBadgeRef,
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,
    searchText,
    setSearchText,
  };
}
